import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { ClassAvailability } from "./ClassAvailability";
import type { ClassLocationStaff } from "./ClassLocationStaff";
import type { JpClassDTOBase } from "./JpClassDTOBase";
import type { JpClassSessionStaff } from "./JpClassSessionStaff";
import type { JpClassSignupStaff } from "./JpClassSignupStaff";
import type { JpClassStaggerStaff } from "./JpClassStaggerStaff";
import type { JpClassTypeStaff } from "./JpClassTypeStaff";
import type { PersonStaff } from "./PersonStaff";
import type { TrackedBase } from "./TrackedBase";

export class JpClassStaff implements JpClassDTOBase, TrackedBase {
  constructor(public readonly instanceId: number, public readonly limitOverride: number | null, public readonly nameOverride: string | null, public readonly maxAge: number | null, public readonly minAge: number | null, public readonly price: number | null, public readonly regCodeRowId: number | null, public readonly confirmTemplate: string | null, public readonly adminHold: string | null, public readonly reservedForGroup: string | null, public readonly overrideNoLimit: string | null, public readonly availability: ClassAvailability | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly typeId: JpClassTypeStaff | null, public readonly locationId: ClassLocationStaff | null, public readonly instructorId: PersonStaff | null, public readonly sessions: Array<JpClassSessionStaff> | null, public readonly signups: Array<JpClassSignupStaff> | null, public readonly staggers: Array<JpClassStaggerStaff> | null) {
    this.cbiServiceContext = null;
    this.typeIdLoaded = this.typeId != null;
    this.typeIdCached = this.typeId;
    this.locationIdLoaded = this.locationId != null;
    this.locationIdCached = this.locationId;
    this.instructorIdLoaded = this.instructorId != null;
    this.instructorIdCached = this.instructorId;
    this.sessionsLoaded = this.sessions != null;
    this.sessionsCached = this.sessions;
    this.signupsLoaded = this.signups != null;
    this.signupsCached = this.signups;
    this.staggersLoaded = this.staggers != null;
    this.staggersCached = this.staggers;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private typeIdLoaded: boolean;
  private typeIdCached: JpClassTypeStaff | null;
  private locationIdLoaded: boolean;
  private locationIdCached: ClassLocationStaff | null;
  private instructorIdLoaded: boolean;
  private instructorIdCached: PersonStaff | null;
  private sessionsLoaded: boolean;
  private sessionsCached: Array<JpClassSessionStaff> | null;
  private signupsLoaded: boolean;
  private signupsCached: Array<JpClassSignupStaff> | null;
  private staggersLoaded: boolean;
  private staggersCached: Array<JpClassStaggerStaff> | null;
  async typeIdLazy(): Promise<JpClassTypeStaff> {
    if (!this.typeIdLoaded) {
      this.typeIdCached = await this.cbiServiceContext!.jpClassService().getJpClassStaffTypeId(this.instanceId);
      this.typeIdLoaded = true;
    }
    return this.typeIdCached!;
  }
  async locationIdLazy(): Promise<ClassLocationStaff | null> {
    if (!this.locationIdLoaded) {
      this.locationIdCached = await this.cbiServiceContext!.jpClassService().getJpClassStaffLocationId(this.instanceId);
      this.locationIdLoaded = true;
    }
    return this.locationIdCached;
  }
  async instructorIdLazy(): Promise<PersonStaff | null> {
    if (!this.instructorIdLoaded) {
      this.instructorIdCached = await this.cbiServiceContext!.jpClassService().getJpClassStaffInstructorId(this.instanceId);
      this.instructorIdLoaded = true;
    }
    return this.instructorIdCached;
  }
  async sessionsLazy(): Promise<Array<JpClassSessionStaff>> {
    if (!this.sessionsLoaded) {
      this.sessionsCached = await this.cbiServiceContext!.jpClassService().getJpClassStaffSessions(this.instanceId);
      this.sessionsLoaded = true;
    }
    return this.sessionsCached!;
  }
  async signupsLazy(): Promise<Array<JpClassSignupStaff>> {
    if (!this.signupsLoaded) {
      this.signupsCached = await this.cbiServiceContext!.jpClassService().getJpClassStaffSignups(this.instanceId);
      this.signupsLoaded = true;
    }
    return this.signupsCached!;
  }
  async staggersLazy(): Promise<Array<JpClassStaggerStaff>> {
    if (!this.staggersLoaded) {
      this.staggersCached = await this.cbiServiceContext!.jpClassService().getJpClassStaffStaggers(this.instanceId);
      this.staggersLoaded = true;
    }
    return this.staggersCached!;
  }
  async summaryLabel(): Promise<string> {
    return await this.cbiServiceContext!.jpClassService().summaryLabelStaff({ instanceId: this.instanceId });
  }
}
